import React, { useState, useEffect } from "react";
import {
  BookOpenCheck,
  CheckCircle,
  Clock,
  XCircle,
  Layers3,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { Box } from "@mui/material";
import clsx from "clsx";
import AdminSidebar from "../Admin/AdminSideBar"; // Ensure the case matches the file name
import AppPagination from "../Tools/AppPagination";
import { getCourses } from "../../State/Course/Action";
import { Star, StarHalf, StarOff } from "lucide-react";
import { useNavigate } from "react-router-dom";

const renderStars = (rating) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <Star
        key={`full-${i}`}
        className="w-4 h-4 text-yellow-500"
        fill="currentColor"
      />
    );
  }

  if (hasHalf) {
    stars.push(
      <StarHalf
        key="half"
        className="w-4 h-4 text-yellow-500"
        fill="currentColor"
      />
    );
  }

  for (let i = 0; i < emptyStars; i++) {
    stars.push(
      <StarOff key={`empty-${i}`} className="w-4 h-4 text-yellow-400" />
    );
  }

  return stars;
};

const TABS = [
  { label: "All Courses", value: "all", icon: <Layers3 /> },
  { label: "Approved", value: "approved", icon: <CheckCircle /> },
  { label: "Pending", value: "pending", icon: <Clock /> },
  { label: "Rejected", value: "rejected", icon: <XCircle /> },
];

const ManageCourses = () => {
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const totalPages = useSelector(
    (store) => store.course.courseListResponse?.totalPages || 0
  );
  const totalCourses = useSelector(
    (store) => store.course.courseListResponse?.totalCourses || 0
  );
  const courses = useSelector(
    (store) => store.course.courseListResponse?.courses || []
  );

  const [approvalStatus, setApprovalStatus] = useState("ALL");

  useEffect(() => {
    dispatch(getCourses(page, approvalStatus)); // make sure your getCourses supports filtering
  }, [page, filter, approvalStatus, dispatch]);

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 to-blue-50">
      <AdminSidebar />
      <div className="flex-1 p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center gap-2">
          <BookOpenCheck size={32} /> Manage Courses
        </h1>
        <p className="text-gray-600 text-sm mb-6">
          Total Courses:{" "}
          <span className="font-semibold text-indigo-700">{totalCourses}</span>
        </p>

        <div className="flex flex-wrap gap-4 mb-8 justify-center">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setFilter(tab.value);
                setPage(0); // Reset to first page when filter changes
                setApprovalStatus(
                  tab.value === "all" ? "ALL" : tab.value.toUpperCase()
                );
              }}
              className={clsx(
                "flex items-center gap-2 px-4 py-2 rounded-full shadow-sm font-medium transition",
                filter === tab.value
                  ? "bg-indigo-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              )}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
        <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {courses.map((course) => (
            <div
              key={course.id}
              className="bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition border border-gray-200"
              onClick={() => {
                navigate(`/course-nested-detail/${course.id}`);
              }}
            >
              {/* Thumbnail */}
              <img
                src={course.thumbnailUrl}
                alt={course.name}
                className="w-full h-40 object-cover rounded-md mb-3"
              />

              {/* Name */}
              <div className="mb-2 text-lg font-semibold text-gray-800 flex items-center gap-2">
                <BookOpenCheck className="text-indigo-500" /> {course.name}
              </div>

              {/* Instructor */}
              <div className="flex items-center gap-2 text-sm text-indigo-600 font-medium mb-2">
                <svg
                  className="w-4 h-4 text-indigo-500"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5.121 17.804A3.001 3.001 0 017 15h10a3.001 3.001 0 011.879 2.804M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  ></path>
                </svg>
                <span>By: {course.instructor}</span>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-600 line-clamp-3 mb-2">
                {course.description}
              </p>

              {/* Price Section */}
              <div className="mb-2 flex items-center gap-2 flex-wrap">
                {(course.discountAmount || course.discount) && (
                  <>
                    <span className="text-sm text-gray-500 line-through">
                      ₹{course.price || 0}
                    </span>
                    <span className="text-base font-bold text-indigo-600">
                      ₹{Math.max(0, (course.price || 0) - (course.discountAmount || course.discount || 0))}
                    </span>
                    <span className="text-xs text-green-600 font-medium">
                      (₹{course.discountAmount || course.discount || 0} Off)
                    </span>
                  </>
                )}
                {!(course.discountAmount || course.discount) && (
                  <span className="text-base font-bold text-indigo-600">
                    ₹{course.price || 0}
                  </span>
                )}
              </div>

              {/* Rating */}
              <div className="flex items-center gap-1">
                {renderStars(course.rating)}
                <span className="text-sm text-gray-500">
                  ({course.rating.toFixed(1)})
                </span>
              </div>
            </div>
          ))}
        </div>

        <Box display="flex" justifyContent="center" mt={3}>
          <AppPagination setPage={setPage} totalPages={totalPages} />
        </Box>
      </div>
    </div>
  );
};

export default ManageCourses;
